import { useState, useCallback } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

interface ToastState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

/**
 * Hook para exibir notificações toast usando MUI Snackbar
 *
 * @example
 * ```tsx
 * const { showError, showSuccess, ToastContainer } = useToast();
 *
 * return (
 *   <>
 *     <button onClick={() => showError('Erro!')}>Mostrar erro</button>
 *     <ToastContainer />
 *   </>
 * );
 * ```
 */
export const useToast = () => {
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'info',
  });

  const showToast = useCallback((message: string, severity: AlertColor = 'info') => {
    setToast({ open: true, message, severity });
  }, []);

  const showError = useCallback((message: string) => {
    showToast(message, 'error');
  }, [showToast]);

  const showSuccess = useCallback((message: string) => {
    showToast(message, 'success');
  }, [showToast]);

  const showWarning = useCallback((message: string) => {
    showToast(message, 'warning');
  }, [showToast]);

  const showInfo = useCallback((message: string) => {
    showToast(message, 'info');
  }, [showToast]);

  const handleClose = useCallback((_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setToast(prev => ({ ...prev, open: false }));
  }, []);

  const ToastContainer = useCallback(() => (
    <Snackbar
      open={toast.open}
      autoHideDuration={6000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert
        onClose={handleClose}
        severity={toast.severity}
        sx={{
          width: '100%',
          bgcolor: toast.severity === 'error' ? '#d32f2f' :
                   toast.severity === 'success' ? '#2e7d32' :
                   toast.severity === 'warning' ? '#ed6c02' : '#0288d1',
          color: '#fff',
          '& .MuiAlert-icon': {
            color: '#fff',
          },
        }}
        variant="filled"
      >
        {toast.message}
      </Alert>
    </Snackbar>
  ), [toast, handleClose]);

  return {
    showToast,
    showError,
    showSuccess,
    showWarning,
    showInfo,
    ToastContainer,
  };
};
